import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ArrowRight, Home as HomeIcon, Fence, Waves, Wrench, Warehouse, Package, Sun, Sparkles, Plus, Layers, ChefHat, Bath } from "lucide-react";

const PROJECT_ICONS = {
  "ADU": HomeIcon, "Deck": Layers, "Fence": Fence, "Pool": Waves,
  "Remodel": Wrench, "Garage": Warehouse, "Shed": Package, "Solar": Sun,
  "Patio": Layers, "Addition": Plus, "Retaining Wall": Layers,
  "Kitchen": ChefHat, "Bathroom": Bath
};

const SD_CITIES = [
  "City of San Diego",
  "Chula Vista",
  "Oceanside",
  "Carlsbad",
  "Encinitas",
  "Escondido",
  "El Cajon",
  "La Mesa",
  "Santee",
  "Poway",
  "Vista",
  "San Marcos",
  "National City",
  "Lemon Grove",
  "Imperial Beach",
  "Solana Beach",
  "Del Mar",
  "Coronado",
  "County of San Diego (Unincorporated)",
];

export default function NewProject() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [address, setAddress] = useState(params.get("address") || "");
  const [addressData, setAddressData] = useState(() => {
    // Rehydrate from query params if hero passed them along
    const city = params.get("city");
    if (city) {
      return {
        full_address: params.get("address") || "",
        city, county: params.get("county") || "",
        lat: params.get("lat") || null, lon: params.get("lon") || null,
      };
    }
    return null;
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pickedCity, setPickedCity] = useState("");
  const [streetInput, setStreetInput] = useState("");

  useEffect(() => {
    api.get("/reference/projects").then(({ data }) => setProjects(data.projects));
  }, []);

  // If we got a query param but no structured data, try to auto-fill via jurisdiction detect
  useEffect(() => {
    if (address && !addressData) {
      api.post("/jurisdiction/detect", { address }).then(({ data }) => {
        setAddressData({ full_address: address, city: null, county: null, jurisdiction: data.jurisdiction });
      });
    }
  }, [address, addressData]);

  const start = async () => {
    setError("");
    if (!selected) return setError("Please select a project type.");

    // If user picked from dropdown + street, use that as the address
    let finalAddress = address;
    let finalCity = addressData?.city;
    if (pickedCity && streetInput) {
      const cityLabel = pickedCity.replace("City of ", "").replace(" (Unincorporated)", "");
      finalAddress = `${streetInput}, ${cityLabel}, CA`;
      finalCity = cityLabel;
    }
    if (!finalAddress) return setError("Please enter an address (either type it or pick a city + street).");

    setBusy(true);
    try {
      const payload = {
        project_type: selected,
        address: finalAddress,
        city: finalCity || undefined,
        county: addressData?.county || undefined,
        lat: addressData?.lat ? parseFloat(addressData.lat) : undefined,
        lon: addressData?.lon ? parseFloat(addressData.lon) : undefined,
      };
      const { data } = await api.post("/projects", payload);
      navigate(`/project/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
      <Seo
        title="Start a New Project — BuildWise AI Permit Analysis"
        description="Kick off a new building project in San Diego. Enter your address, choose project type, and let BuildWise AI generate a permit-ready feasibility report in minutes."
        path="/new"
      />
      <Header />
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-12">
        <div className="label-overline mb-3">New Project</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-3" style={{ color: "var(--bw-text-primary)" }}>
          What are you building?
        </h1>
        <p className="text-lg mb-10 max-w-2xl leading-relaxed" style={{ color: "var(--bw-text-secondary)" }}>
          Choose a project type and tell us where. BuildWise AI will interview you about size, materials, and constraints,
          then map your project against the correct San Diego jurisdiction's permit requirements — no guesswork.
        </p>

        {/* Address */}
        <div className="bw-card p-8 mb-10">
          <label className="label-overline block mb-4">Property Address</label>
          <AddressAutocomplete
            value={address}
            onChange={(v) => { setAddress(v); }}
            onSelect={(s) => { setAddress(s.full_address); setAddressData(s); }}
            variant="mega"
            placeholder="Start typing any San Diego County address…"
            testid="new-address"
          />
          <p className="mt-3 text-xs" style={{ color: "var(--bw-text-tertiary)" }}>
            Powered by OpenStreetMap · Suggestions restricted to San Diego County
          </p>

          {/* OR Divider */}
          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px" style={{ backgroundColor: "var(--bw-border)" }} />
            <span className="text-xs uppercase font-bold tracking-widest" style={{ color: "var(--bw-text-tertiary)" }}>Or pick from dropdown</span>
            <div className="flex-1 h-px" style={{ backgroundColor: "var(--bw-border)" }} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label-overline block mb-2">City / Jurisdiction</label>
              <select
                value={pickedCity}
                onChange={(e) => setPickedCity(e.target.value)}
                className="bw-input"
                data-testid="city-dropdown"
              >
                <option value="">-- Select a city --</option>
                {SD_CITIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-overline block mb-2">Street Address</label>
              <input
                type="text"
                value={streetInput}
                onChange={(e) => setStreetInput(e.target.value)}
                placeholder="e.g., 1234 Main St"
                className="bw-input"
                data-testid="street-input"
              />
            </div>
          </div>

          {pickedCity && streetInput && (
            <div className="mt-4 text-sm p-3 border" style={{ borderColor: "var(--bw-success)", color: "var(--bw-success)" }} data-testid="picked-preview">
              Using: <strong>{streetInput}, {pickedCity.replace("City of ", "").replace(" (Unincorporated)", "")}, CA</strong> · {pickedCity}
            </div>
          )}
        </div>

        {/* Project Grid */}
        <div className="label-overline mb-4">Select Project Type</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-10">
          {projects.map((p) => {
            const Icon = PROJECT_ICONS[p] || Sparkles;
            return (
              <button
                key={p}
                onClick={() => setSelected(p)}
                className={`project-tile ${selected === p ? "selected" : ""}`}
                data-testid={`project-select-${p.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-7 h-7" strokeWidth={1.5} />
                <span className="font-semibold text-sm">{p}</span>
              </button>
            );
          })}
        </div>

        {error && (
          <div className="mb-6 p-3 border text-sm" style={{ borderColor: "var(--bw-error)", color: "var(--bw-error)" }} data-testid="new-error">
            {error}
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-3">
          <button onClick={start} disabled={busy} className="btn-primary" data-testid="start-analysis-btn">
            {busy ? "Starting…" : "Start AI Interview"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
